import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEncryptedParams } from "../../hooks/useEncryptedParams";
import { encrypt } from "../../utils/encryption";
import Button from "../../components/common/Button";
import DataTable from "../../components/common/DataTable";
import SearchableDropdown from "../../components/common/SearchableDropdown";
import Modal from "../../components/common/Modal";
import purchaseMasterService from "../../services/purchaseMasterService";
import reportService from "../../services/reportService";
import { formatDate, formatDateTime } from "../../utils/dateUtils";

const BL_STAGES = ["SI Pending", "Loading", "In Transit", "Arrived", "Arrived ICD"];

function formatNumber(value) {
  const number = Number(value || 0);
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(number);
}

function downloadBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function parseCsv(text) {
  const rows = [];
  let row = [];
  let value = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    const next = text[i + 1];

    if (inQuotes) {
      if (char === '"' && next === '"') {
        value += '"';
        i += 1;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        value += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
      continue;
    }

    if (char === ",") {
      row.push(value);
      value = "";
      continue;
    }

    if (char === "\n") {
      row.push(value);
      rows.push(row);
      row = [];
      value = "";
      continue;
    }

    if (char !== "\r") {
      value += char;
    }
  }

  if (value.length || row.length) {
    row.push(value);
    rows.push(row);
  }

  return rows;
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function normalizeHexColor(value) {
  if (!value) return "";
  const trimmed = String(value).trim();
  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) return trimmed;
  return "";
}

function buildColorfulExcelFromCsv(csvText) {
  const rows = parseCsv(csvText);
  if (!rows.length) {
    return new Blob([""], { type: "application/vnd.ms-excel;charset=utf-8;" });
  }

  const headerRowIndex = rows.findIndex((row) => row.includes("Current Status"));
  const currentStatusIndex = headerRowIndex >= 0 ? rows[headerRowIndex].indexOf("Current Status") : -1;

  // Extract colors from row 2 (index 2) to determine column colors
  const stageColorsByColumn = [];
  if (rows[2]) {
    let activeColor = "";
    rows[2].forEach((cell, i) => {
      const hex = normalizeHexColor(cell);
      if (hex) activeColor = hex;
      stageColorsByColumn[i] = activeColor;
    });
  }

  const excelStatusColors = {
    'SI Pending': { bg: '#fef3c7', text: '#a16207' },
    'Loading': { bg: '#dbeafe', text: '#1d4ed8' },
    'In Transit': { bg: '#ede9fe', text: '#6d28d9' },
    'Arrived': { bg: '#dcfce7', text: '#15803d' },
    'Arrived ICD': { bg: '#ccfbf1', text: '#0f766e' }
  };

  const htmlRows = rows.map((row, rowIndex) => {
    // Skip the hex color row entirely from the final output
    if (rowIndex === 2) return null;

    // Preamble Rows (Row 0 and 1)
    if (rowIndex < headerRowIndex) {
      let html = "<tr>";
      for (let i = 0; i < row.length; i++) {
        let cellContent = row[i];
        let colspan = 1;

        // Group empty cells together for spans
        while (i + 1 < row.length && row[i + 1] === "") {
          colspan++;
          i++;
        }

        let bg = "";
        let color = "";
        let text = escapeHtml(cellContent);
        let fontSize = "12px"; // Default for body is handled in table, but we can be explicit

        if (rowIndex === 1) {
          // Stage Titles
          bg = stageColorsByColumn[i] || "";
          color = bg ? (bg.toUpperCase() === "#FFC000" ? "#000000" : "#ffffff") : "";
          fontSize = "10px";
        } else if (rowIndex === 0) {
          // Main Title
          colspan = stageColorsByColumn.length > 0 ? stageColorsByColumn.length : row.length;
          bg = "#f8fafc";
          fontSize = "12px";
        }

        const styles = [
          "border:1px solid #cbd5e1",
          "padding:8px",
          "text-align:center",
          "font-weight:700",
          `font-size:${fontSize}`,
          bg ? `background-color:${bg}` : "",
          color ? `color:${color}` : ""
        ].filter(Boolean).join(";");

        html += `<td colspan="${colspan}" style="${styles}">${text}</td>`;
      }
      html += "</tr>";
      return html;
    }

    // Header and Data Rows (rowIndex >= headerRowIndex)
    const isHeader = rowIndex === headerRowIndex;
    const statusText = row[currentStatusIndex] || "";
    const statusStyle = excelStatusColors[statusText];

    let html = "<tr>";
    row.forEach((cell, cellIndex) => {
      let bg = "";
      let color = "";
      let weight = "";
      let textAlign = "left";

      if (isHeader) {
        bg = stageColorsByColumn[cellIndex] || "";
        color = bg ? (bg.toUpperCase() === "#FFC000" ? "#000000" : "#ffffff") : "";
        weight = "font-weight:700";
        textAlign = "center";
      } else if (cellIndex === currentStatusIndex && statusStyle) {
        bg = statusStyle.bg;
        color = statusStyle.text;
        weight = "font-weight:700";
        textAlign = "center";
      }

      const styles = [
        "border:1px solid #cbd5e1",
        "padding:6px 8px",
        `text-align:${textAlign}`,
        weight,
        bg ? `background-color:${bg}` : "",
        color ? `color:${color}` : ""
      ].filter(Boolean).join(";");

      html += `<td style="${styles}">${escapeHtml(cell)}</td>`;
    });
    html += "</tr>";
    return html;
  }).filter(Boolean);

  const html = `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="utf-8" />
<style>
  table { border-collapse: collapse; font-family: Calibri, Arial, sans-serif; font-size: 12px; }
</style>
</head>
<body>
<table>
${htmlRows.join("\n")}
</table>
</body>
</html>`;

  return new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" });
}

function getPurchaseMasterId(item) {
  return item.purchaseMasterId || item.id;
}

function getPurchaseMasterLabel(item) {
  const parts = [];
  if (item.bookingNo) parts.push(`Booking: ${item.bookingNo}`);
  if (item.billNo || item.purchaseInvoiceNo) parts.push(`Bill: ${item.billNo || item.purchaseInvoiceNo}`);
  
  if (parts.length > 0) {
    return parts.join(" | ");
  }
  
  return `Purchase Master ${getPurchaseMasterId(item)}`;
}

function PurchaseMasterWiseReportPage() {
  const navigate = useNavigate();
  const { purchaseMasterId } = useEncryptedParams();
  const [selectedPurchaseMasterId, setSelectedPurchaseMasterId] = useState(purchaseMasterId || "");
  const [activePurchaseMasters, setActivePurchaseMasters] = useState([]);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPurchaseMastersLoading, setIsPurchaseMastersLoading] = useState(false);
  const [downloadingFormat, setDownloadingFormat] = useState("");
  const [error, setError] = useState("");
  const [selectedRowForActivity, setSelectedRowForActivity] = useState(null);

  useEffect(() => {
    setSelectedPurchaseMasterId(purchaseMasterId || "");
  }, [purchaseMasterId]);

  const loadActivePurchaseMasters = useCallback(async () => {
    setIsPurchaseMastersLoading(true);
    try {
      const data = await purchaseMasterService.getActivePurchaseMasters();
      setActivePurchaseMasters(data.items || []);
    } catch (err) {
      setError(err?.message || "Failed to load active purchase masters.");
    } finally {
      setIsPurchaseMastersLoading(false);
    }
  }, []);

  const loadReport = useCallback(async (currentPurchaseMasterId) => {
    setIsLoading(true);
    setError("");
    try {
      const response = await reportService.getPurchaseMasterWiseReport(currentPurchaseMasterId);
      setRows(response.items || []);
    } catch (err) {
      setRows([]);
      setError(err?.message || "Failed to load purchase master-wise report.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadActivePurchaseMasters();
  }, [loadActivePurchaseMasters]);

  useEffect(() => {
    loadReport(purchaseMasterId);
  }, [purchaseMasterId, loadReport]);

  const purchaseMasterOptions = useMemo(() => {
    const options = activePurchaseMasters.map((item) => ({
      value: String(getPurchaseMasterId(item)),
      label: getPurchaseMasterLabel(item),
    }));
    return [{ value: "", label: "All" }, ...options];
  }, [activePurchaseMasters]);

  const columns = useMemo(
    () => [
      // STAGE 1 — BOOKING (Blue)
      {
        key: "sr_no",
        label: "Sr. No.",
        headerStyle: { backgroundColor: "#2F75B5", color: "white" },
        style: { backgroundColor: "#2F75B5", color: "white" },
        render: (_, index) => index + 1,
      },
      {
        key: "bookingNo",
        label: "Booking No.",
        sortable: true,
        headerStyle: { backgroundColor: "#2F75B5", color: "white" },
        style: { backgroundColor: "#2F75B5", color: "white" },
        render: (item) => String(item.bookingNo || "-"),
      },
      {
        key: "blNo",
        label: "BL Number",
        sortable: true,
        headerStyle: { backgroundColor: "#2F75B5", color: "white" },
        style: { backgroundColor: "#2F75B5", color: "white" },
        render: (item) => String(item.blNo || "-"),
      },
      {
        key: "blWeight",
        label: "BL Weight (KG)",
        sortable: true,
        headerStyle: { backgroundColor: "#2F75B5", color: "white" },
        headerClassName: "text-right",
        className: "text-right font-mono",
        style: { backgroundColor: "#2F75B5", color: "white" },
        render: (item) => formatNumber(item.blWeight || 0),
      },
      {
        key: "shippingLine",
        label: "Shipping Line",
        sortable: true,
        headerStyle: { backgroundColor: "#2F75B5", color: "white" },
        style: { backgroundColor: "#2F75B5", color: "white" },
        render: (item) => String(item.shippingLine || "-"),
      },
      {
        key: "supplier",
        label: "Supplier (USA)",
        sortable: true,
        headerStyle: { backgroundColor: "#2F75B5", color: "white" },
        style: { backgroundColor: "#2F75B5", color: "white" },
        render: (item) => String(item.supplier || "-"),
      },

      // STAGE 2 — LOADING (Green)
      {
        key: "containerNo",
        label: "Container No.",
        sortable: true,
        headerStyle: { backgroundColor: "#548235", color: "white" },
        style: { backgroundColor: "#548235", color: "white" },
        render: (item) => String(item.containerNo || "-"),
      },
      {
        key: "material",
        label: "Material",
        sortable: true,
        headerStyle: { backgroundColor: "#548235", color: "white" },
        style: { backgroundColor: "#548235", color: "white" },
        render: (item) => String(item.material || "-"),
      },
      {
        key: "qtyKG",
        label: "Qty (KG)",
        sortable: true,
        headerStyle: { backgroundColor: "#548235", color: "white" },
        headerClassName: "text-right",
        className: "text-right font-mono",
        style: { backgroundColor: "#548235", color: "white" },
        render: (item) => formatNumber(item.qtyKG || 0),
      },
      {
        key: "purchaseBillNo",
        label: "Purchase Invoice No.",
        sortable: true,
        headerStyle: { backgroundColor: "#548235", color: "white" },
        style: { backgroundColor: "#548235", color: "white" },
        render: (item) => String(item.purchaseBillNo || "-"),
      },
      {
        key: "purchaseBillDate",
        label: "Bill Date",
        sortable: true,
        headerStyle: { backgroundColor: "#548235", color: "white" },
        style: { backgroundColor: "#548235", color: "white" },
        render: (item) => formatDate(item.purchaseBillDate),
      },

      // STAGE 4 — TRANSIT (Yellow)
      {
        key: "dateOfDeparture",
        label: "Date of Departure",
        sortable: true,
        headerStyle: { backgroundColor: "#FFC000", color: "black" },
        style: { backgroundColor: "#FFC000", color: "black" },
        render: (item) => formatDate(item.dateOfDeparture),
      },
      {
        key: "eta",
        label: "ETA",
        sortable: true,
        headerStyle: { backgroundColor: "#FFC000", color: "black" },
        style: { backgroundColor: "#FFC000", color: "black" },
        render: (item) => formatDate(item.eta),
      },
      {
        key: "etaIcd",
        label: "ETA ICD",
        sortable: true,
        headerStyle: { backgroundColor: "#FFC000", color: "black" },
        style: { backgroundColor: "#FFC000", color: "black" },
        render: (item) => formatDate(item.etaIcd),
      },
      {
        key: "daysLeftForIcd",
        label: "Days Left for ICD",
        sortable: true,
        headerStyle: { backgroundColor: "#FFC000", color: "black" },
        headerClassName: "text-right",
        className: "text-right font-bold",
        style: { backgroundColor: "#FFC000", color: "black" },
        render: (item) => String(item.daysLeftForIcd || "-"),
      },
      {
        key: "blReceivedStage4",
        label: "BL Received",
        sortable: true,
        headerStyle: { backgroundColor: "#FFC000", color: "black" },
        style: { backgroundColor: "#FFC000", color: "black" },
        render: (item) => String(item.blReceivedStage4 || "No"),
      },

      // STAGE 5 — ICD FREE DAYS (Orange)
      {
        key: "freeDays",
        label: "Free Days",
        sortable: true,
        headerStyle: { backgroundColor: "#C55A11", color: "white" },
        headerClassName: "text-right",
        className: "text-right font-bold",
        style: { backgroundColor: "#C55A11", color: "white" },
        render: (item) => String(item.freeDays || "0"),
      },
      {
        key: "daysLeftFree",
        label: "Days Left Free",
        sortable: true,
        headerStyle: { backgroundColor: "#C55A11", color: "white" },
        headerClassName: "text-right",
        className: "text-right font-bold",
        style: { backgroundColor: "#C55A11", color: "white" },
        render: (item) => {
          const days = item.daysLeftFree;
          if (days === undefined || days === null) return "-";
          const numericDays = Number(days);
          if (!isNaN(numericDays)) {
            if (numericDays < 0)
              return <span className="font-bold text-white">Overdue ({Math.abs(numericDays)})</span>;
            if (numericDays === 0)
              return <span className="font-bold text-white animate-pulse">TODAY</span>;
            return <span className="text-white">{numericDays} Days</span>;
          }
          return <span className="text-white">{days}</span>;
        },
      },

      // STAGE 6 — STATUS (Purple)
      {
        key: "currentStatus",
        label: "Current Status",
        sortable: true,
        headerStyle: { backgroundColor: "#7030A0", color: "white" },
        style: { backgroundColor: "#7030A0", color: "white" },
        render: (item) => {
          const status = String(item?.currentStatus || "SI Pending");
          const colors = {
            "SI Pending": "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400",
            Loading: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400",
            "In Transit": "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400",
            Arrived: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
            "Arrived ICD": "bg-teal-100 text-teal-700 dark:bg-teal-500/20 dark:text-teal-400",
          };
          return (
            <button
              onClick={() => setSelectedRowForActivity(item)}
              className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider transition-transform hover:scale-105 ${colors[status] || colors["SI Pending"]}`}
            >
              {status}
            </button>
          );
        },
      },
      {
        key: "blReceivedStage6",
        label: "BL Received",
        sortable: true,
        headerStyle: { backgroundColor: "#7030A0", color: "white" },
        style: { backgroundColor: "#7030A0", color: "white" },
        render: (item) => String(item.blReceivedStage6 || "No"),
      },
      {
        key: "remarks",
        label: "Remarks",
        sortable: true,
        headerStyle: { backgroundColor: "#7030A0", color: "white" },
        style: { backgroundColor: "#7030A0", color: "white" },
        render: (item) => String(item.remarks || "-"),
      },
    ],
    []
  );

  const handlePurchaseMasterChange = (event) => {
    const value = event.target.value;
    setSelectedPurchaseMasterId(value);
    navigate(
      value ? `/reports/purchase-master-wise/${encrypt(value)}` : "/reports/purchase-master-wise"
    );
  };

  const handleRefresh = () => {
    loadActivePurchaseMasters();
    loadReport(selectedPurchaseMasterId);
  };

  const handleDownload = async (format) => {
    setDownloadingFormat(format);
    setError("");
    try {
      const blob = await reportService.downloadPurchaseMasterWiseReport(
        format,
        selectedPurchaseMasterId
      );
      const scope = selectedPurchaseMasterId ? `purchase-master-${selectedPurchaseMasterId}` : "all";
      if (format === "csv") {
        const csvText = await blob.text();
        const colorfulBlob = buildColorfulExcelFromCsv(csvText);
        downloadBlob(colorfulBlob, `purchase-master-wise-report-${scope}.xls`);
      } else {
        downloadBlob(blob, `purchase-master-wise-report-${scope}.${format}`);
      }
    } catch (err) {
      setError(err?.message || `Failed to download ${format.toUpperCase()} report.`);
    } finally {
      setDownloadingFormat("");
    }
  };

  const filteredRows = useMemo(() => {
    if (!selectedPurchaseMasterId) return rows;
    return rows.filter((row) => String(row.purchaseMasterId) === String(selectedPurchaseMasterId));
  }, [rows, selectedPurchaseMasterId]);

  const summary = useMemo(() => {
    return filteredRows.reduce(
      (acc, row) => {
        // Calculate Total Weight directly from row quantities
        acc.totalWeight += Number(row.qtyKG || 0);
        
        // Calculate Unique BLs
        if (row.blNo && !acc.uniqueBls.has(row.blNo)) {
          acc.uniqueBls.add(row.blNo);
          acc.totalBls += 1;
        }
        
        return acc;
      },
      { totalWeight: 0, totalBls: 0, uniqueBls: new Set() }
    );
  }, [filteredRows]);

  const selectedStatus = selectedRowForActivity?.currentStatus || "SI Pending";
  const selectedStageIndex = Math.max(BL_STAGES.indexOf(selectedStatus), 0);
  const progressPercent = Math.round(((selectedStageIndex + 1) / BL_STAGES.length) * 100);
  const lastUpdated =
    selectedRowForActivity?.modifiedDate || selectedRowForActivity?.createdDate;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 border-b border-slate-200 pb-4 dark:border-slate-800 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Reports
          </p>
          <h1 className="mt-1 text-2xl font-bold text-slate-950 dark:text-white">
            Purchase Master-wise Report
          </h1>
        </div>

        <div className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto lg:items-end">
          <div className="w-full sm:min-w-[220px]">
            <SearchableDropdown
              label="Active Purchase Master"
              name="purchaseMasterId"
              value={selectedPurchaseMasterId}
              onChange={handlePurchaseMasterChange}
              options={purchaseMasterOptions}
              placeholder={
                isPurchaseMastersLoading
                  ? "Loading purchase masters..."
                  : "All Purchases"
              }
              disabled={isPurchaseMastersLoading}
            />
          </div>
          <Button
            variant="secondary"
            onClick={handleRefresh}
            isLoading={isLoading || isPurchaseMastersLoading}
          >
            Refresh
          </Button>
          <Button
            variant="secondary"
            onClick={() => handleDownload("csv")}
            isLoading={downloadingFormat === "csv"}
            disabled={Boolean(downloadingFormat)}
          >
            CSV
          </Button>
          <Button
            variant="secondary"
            onClick={() => handleDownload("pdf")}
            isLoading={downloadingFormat === "pdf"}
            disabled={Boolean(downloadingFormat)}
          >
            PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Total BLs</p>
          <p className="mt-1 text-2xl font-black text-slate-900 dark:text-white">{summary.totalBls}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Total Weight (MT)</p>
          <p className="mt-1 text-2xl font-black text-primary-600">
            {formatNumber(summary.totalWeight / 1000)} MT
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Total Weight (KG)</p>
          <p className="mt-1 text-2xl font-black text-emerald-600">
            {formatNumber(summary.totalWeight)} KG
          </p>
        </div>
      </div>

      {error ? (
        <div className="rounded-lg border border-danger/30 bg-danger/5 px-4 py-3 text-sm font-medium text-danger">
          {error}
        </div>
      ) : null}

      <DataTable
        columns={columns}
        data={filteredRows}
        isLoading={isLoading}
        searchPlaceholder="Search purchase master report..."
        pageSize={10}
        scrollHeight="58vh"
      />

      <Modal
        isOpen={Boolean(selectedRowForActivity)}
        onClose={() => setSelectedRowForActivity(null)}
        title="BL Shipment Status"
      >
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 dark:border-slate-700 dark:bg-slate-900/40">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] text-slate-500 uppercase font-bold">BL Number</p>
                <p className="text-sm font-black">
                  {selectedRowForActivity?.blNumber || selectedRowForActivity?.blNo || "-"}
                </p>
              </div>
              <div>
                <p className="text-[10px] text-slate-500 uppercase font-bold">Current Stage</p>
                <p className="text-sm font-black text-primary-600">{selectedStatus}</p>
              </div>
            </div>
          </div>

          <div className="px-1">
            <div className="flex justify-between items-end mb-2">
              <span className="text-xs font-black text-slate-800 dark:text-white uppercase tracking-tight">
                Shipping Progress
              </span>
              <span className="text-xs font-black text-primary-600">{progressPercent}%</span>
            </div>
            <div className="h-2 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500 rounded-full transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
            {BL_STAGES.map((stage, index) => {
              const isDone = index < selectedStageIndex;
              const isActive = index === selectedStageIndex;
              return (
                <div
                  key={stage}
                  className={`flex gap-3 p-3 rounded-xl border transition-all ${
                    isActive
                      ? "bg-primary-50 border-primary-200 dark:bg-primary-900/10 dark:border-primary-800"
                      : "bg-white border-slate-100 dark:bg-slate-900 dark:border-slate-800"
                  }`}
                >
                  <div className="flex flex-col items-center">
                    <div
                      className={`h-6 w-6 rounded-full flex items-center justify-center border-2 ${
                        isDone
                          ? "bg-emerald-500 border-emerald-500"
                          : isActive
                            ? "border-primary-600 bg-white dark:bg-slate-900"
                            : "border-slate-200"
                      }`}
                    >
                      {isDone ? (
                        <svg
                          className="h-4 w-4 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      ) : (
                        <div
                          className={`h-1.5 w-1.5 rounded-full ${
                            isActive ? "bg-primary-500 animate-pulse" : "bg-slate-200"
                          }`}
                        />
                      )}
                    </div>
                    {index < BL_STAGES.length - 1 && (
                      <div
                        className={`w-0.5 h-full min-h-[10px] mt-1 ${
                          isDone ? "bg-emerald-500" : "bg-slate-100 dark:bg-slate-800"
                        }`}
                      />
                    )}
                  </div>
                  <div className="flex-1">
                    <p
                      className={`text-xs font-black uppercase tracking-tight ${
                        isActive
                          ? "text-primary-700 dark:text-primary-400"
                          : isDone
                            ? "text-emerald-700 dark:text-emerald-400"
                            : "text-slate-400"
                      }`}
                    >
                      {stage}
                    </p>
                    {isActive && (
                      <p className="text-[10px] text-primary-600/70 font-semibold italic mt-0.5">
                        Current Status
                      </p>
                    )}
                    {(isDone || isActive) && (
                      <p className="text-[9px] text-slate-500 mt-1">
                        Updated {formatDateTime(lastUpdated)}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="rounded-md border border-blue-100 bg-blue-50 px-2 py-1.5 text-[10px] text-slate-600 dark:border-blue-400/20 dark:bg-blue-500/10 dark:text-slate-300">
            All dates and times are shown in your local timezone.
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default PurchaseMasterWiseReportPage;
